import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useIntl, defineMessages } from "react-intl";
import { TextField, IconButton, Popper } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { TableCell, TableRow } from "@material-ui/core";
import { Value } from "./workOrderDetails";
import DeleteIcon from "../common/icons/deleteIcon";
import { getLocalizedNameWithIntl } from "../../globals/langHelpers";
import { WINTER_PERIOD_PREMIUM } from "./skuList";

const labels = defineMessages({
    close: {
        id: "admin.work-order-details.close",
        defaultMessage: "Close",
        description: "Admin work order close text",
    },
    open: {
        id: "admin.work-order-details.open",
        defaultMessage: "Open",
        description: "Admin work order open",
    },
    noOptions: {
        id: "admin.work-order-details.no-options",
        defaultMessage: "No options",
        description: "Admin work order no options",
    },
});

const PopperSKu = function (props) {
    return <Popper {...props} style={{ minWidth: 300 }} placement="top-start" />;
};
const useStyles = makeStyles({
    winterStyle: {
        color: "#0075FF",
    },
});

const SKURowEdit = (props) => {
    const intl = useIntl();
    const classes = useStyles();
    const { onQuantityChange, activities, removeSKU, billingReasons, updatedSKU, setUpdatedSKU } = props;
    const [row, setRow] = useState(props.row);
    const [billingReason, setBillingReason] = useState("");
    const styleChange = row.winterElligibleSKU;
    const WinterCheckbox = props.checkbox;
    const [, refreshState] = useState({});

    const update = () => {
        row.dirty = true;
        refreshState({});
    };

    const getChangedRow = (row) => {
        onQuantityChange(row);
    };
    const removeUnderGroundCabelReasons = useSelector((state) => state.reasons.partialReasons.get("CopperDecomBillingReason") ?? []);
    const activity = activities.find((a) => a.productCode === row.productCode);
    const price = row?.price ? row?.price : activity?.price;
    const ddActivities = activities;

    const removeUndergroundCabelSkus = useMemo(
        () => (process.env.REACT_APP_REMOVE_UNDERGROUND_CABLE_SKU || "").split(",").filter((sku) => !!sku.trim()),
        [process.env.REACT_APP_REMOVE_UNDERGROUND_CABLE_SKU],
    );

    return (
        <TableRow>
            <TableCell align="left">
                <Autocomplete
                    id="activities-combo-box"
                    fullWidth
                    PopperComponent={PopperSKu}
                    value={row}
                    closeText={intl.formatMessage(labels.close)}
                    openText={intl.formatMessage(labels.open)}
                    noOptionsText={intl.formatMessage(labels.noOptions)}
                    onChange={(event, newValue) => {
                        if (!newValue) return;
                        const activity = activities.find((a) => a.productCode === newValue.productCode);
                        const newRow = row;
                        newRow.productCode = activity.productCode;
                        newRow.unitOfMeasure = activity.unitOfMeasure;
                        newRow.productDescription_en = activity.productDescription_en;
                        newRow.productDescription_fr = activity.productDescription_fr;
                        newRow.price = activity.price;
                        newRow.winterElligibleSKU = activity.winterPricingEligibility;
                        newRow.isBillingReasonRequired = activity.isBillingReasonRequired;
                        setRow(newRow);
                        update();
                        getChangedRow(row);
                        setUpdatedSKU({ ...updatedSKU, productCodeValue: row?.productCode });
                    }}
                    options={ddActivities.filter((item) => item.productCode != WINTER_PERIOD_PREMIUM)}
                    getOptionLabel={(option) => {
                        var name = getLocalizedNameWithIntl(intl, { object: option, value: "productDescription" });
                        if (option.productCode) return `${option.productCode} - ${name}`;
                        else return "";
                    }}
                    renderInput={(params) => <TextField {...params} size="small" variant="outlined" error={!row.productCode} />}
                />
            </TableCell>
            <TableCell align="left">{getLocalizedNameWithIntl(intl, { object: row, value: "productDescription" })}</TableCell>
            <TableCell align="left">
                {row?.productCode === activity?.productCode && activity?.isBillingReasonRequired ? (
                    <Autocomplete
                        id="billing-reasons"
                        fullWidth
                        PopperComponent={PopperSKu}
                        value={billingReason ? billingReason : row?.billingReasonCode}
                        closeText={intl.formatMessage(labels.close)}
                        openText={intl.formatMessage(labels.open)}
                        noOptionsText={intl.formatMessage(labels.noOptions)}
                        options={removeUndergroundCabelSkus.includes(row?.productCode) ? removeUnderGroundCabelReasons : billingReasons}
                        getOptionLabel={(option) => getLocalizedNameWithIntl(intl, { object: option, value: "name" })}
                        onChange={(event, newValue) => {
                            const newRow = row;
                            newRow.billingReason = newValue?.id;
                            newRow.billingReasonCode = newValue;
                            setRow(newRow);
                            update();
                            setBillingReason(newValue);
                            getChangedRow(row);
                            setUpdatedSKU({
                                ...updatedSKU,
                                billingReasonValue: getLocalizedNameWithIntl(intl, { object: row?.billingReasonCode, value: "name" }),
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                variant="outlined"
                                error={!getLocalizedNameWithIntl(intl, { object: row?.billingReasonCode, value: "name" })}
                            />
                        )}
                    />
                ) : null}
            </TableCell>
            <TableCell align="left">
                <TextField
                    variant="outlined"
                    fullWidth
                    value={row.additionalDetails}
                    error={row?.productCode === activity?.productCode && activity?.isBillingReasonRequired ? !row.additionalDetails : null}
                    onChange={(event) => {
                        const newRow = row;
                        newRow.additionalDetails = event.target.value;
                        setRow(newRow);
                        update();
                        getChangedRow(row);
                        setUpdatedSKU({ ...updatedSKU, addtionalDetailValue: row?.additionalDetails });
                    }}
                />
            </TableCell>
            <TableCell align="right">
                <TextField
                    variant="outlined"
                    placeholder="Quantity"
                    type="number"
                    fullWidth
                    value={!!row.quantity || row.quantity === 0 ? parseFloat(row.quantity) : ""}
                    error={isNaN(parseFloat(row.quantity)) || parseFloat(row.quantity) === 0}
                    onChange={(event) => {
                        let value = parseFloat(event.target.value);
                        if (/^(?:\d*\.\d{1,2}|\d+)$/.test(value)) {
                            row.quantity = !!value ? value : null;
                            row.cost = Number(value * price)?.toFixed(2);
                        } else {
                            row.quantity = !!event?.target?.value ? parseFloat(event?.target?.value)?.toFixed(2) : null;
                            row.cost = Number(event?.target?.value * price)?.toFixed(2);
                        }
                        setUpdatedSKU({ ...updatedSKU, quantityValue: parseFloat(row.quantity) });
                        update();
                        getChangedRow(row);
                    }}
                    InputProps={{ inputProps: { min: 0.01, step: 0.01 } }}
                />
            </TableCell>
            <TableCell align="right">
                <Value>{row.unitOfMeasure}</Value>
            </TableCell>
            <TableCell align="right" className={styleChange && WinterCheckbox ? classes.winterStyle : ""}>
                {price}
            </TableCell>
            <TableCell align="right" className={styleChange && WinterCheckbox ? classes.winterStyle : ""}>
                {Number(row.quantity * price).toFixed(2)}
            </TableCell>
            <TableCell align="center">
                <IconButton
                    style={{
                        padding: "unset",
                    }}
                    onClick={() => removeSKU()}>
                    <DeleteIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default SKURowEdit;
